/**
 * Server functions لإدارة "تقويم حملات الهيرو" — جدول `hero_campaigns`.
 * الثوابت/الأنواع/المساعدات في `heroCampaigns.shared.ts` (هذا الملفّ wrapper رقيق).
 */
import { createServerFn } from "@tanstack/react-start";
import { lazyValidator } from "@/lib/server/lazy-validator";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  normalizeMarket,
  type HeroCampaign,
  type HeroHeightMode,
  type HeroTextPosition,
} from "./heroCampaigns.shared";

export type {
  HeroCampaign,
  HeroCampaignMarket,
  HeroHeightMode,
  HeroTextPosition,
} from "./heroCampaigns.shared";

/**
 * Runtime: يختار حملة واحدة فقط — نفس منطق ORDER BY في الخطّة.
 * `device` (اختياريّ): عند تمريره نُفضّل أعلى حملة تملك فعلاً وسيطاً لهذا
 * المقاس، حتى يمكن تشغيل حملة موبايل وحملة ديسكتوب في نفس الوقت.
 */
export const getActiveHeroCampaign = createServerFn({ method: "GET" })
  .inputValidator(lazyValidator(() => import("./heroCampaigns.schemas").then((m) => m.heroActiveSchema), {}))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const market = normalizeMarket(data.market);
    const page = data.page || "home";
    const nowIso = new Date().toISOString();

    const { data: rows, error } = await supabaseAdmin
      .from("hero_campaigns")
      .select(
        "id, name, market, desktop_url, mobile_url, poster_url, starts_at, ends_at, priority, height_mode, media_opacity, text_position, title_ar, title_en, subtitle_ar, subtitle_en, cta_label_ar, cta_label_en, cta_href, scroll_cta_label_ar, scroll_cta_label_en, scroll_cta_href, scroll_cta_enabled",
      )
      .eq("page", page)
      .eq("enabled", true)
      .in("market", [market, "*"])
      .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
      .or(`ends_at.is.null,ends_at.gt.${nowIso}`);

    if (error || !rows || rows.length === 0) return null;

    // حملة بلا أيّ وسيط (لا فيديو ولا صورة ولا غلاف) = برواز فارغ:
    // تُتجاهل تماماً حتى لا تحجب وسائط «عناصر الصفحة» وتُظهر هيرو أسود.
    const withMedia = rows.filter((r) => !!(r.desktop_url || r.mobile_url || r.poster_url));
    if (withMedia.length === 0) return null;



    // ترتيب يدويّ: السوق المحدّد > "*"، ثمّ priority DESC، ثمّ starts_at الأحدث.
    const sorted = [...withMedia].sort((a, b) => {
      if (a.market !== b.market) {
        if (a.market === market) return -1;
        if (b.market === market) return 1;
      }
      if (a.priority !== b.priority) return b.priority - a.priority;
      const at = a.starts_at ? new Date(a.starts_at).getTime() : 0;
      const bt = b.starts_at ? new Date(b.starts_at).getTime() : 0;
      return bt - at;
    });

    // اختيار حسب المقاس: أوّل حملة تملك وسيطاً لهذا المقاس، وإلّا الأعلى ترتيباً.
    const c =
      (data.device === "mobile"
        ? sorted.find((r) => !!r.mobile_url)
        : data.device === "desktop"
          ? sorted.find((r) => !!r.desktop_url)
          : undefined) ?? sorted[0];
    return {
      id: c.id,
      name: c.name,
      desktop: c.desktop_url ?? "",
      mobile: c.mobile_url ?? "",
      poster: c.poster_url ?? "",
      height_mode: (c.height_mode as HeroHeightMode) ?? "full",
      media_opacity: typeof c.media_opacity === "number" ? c.media_opacity : 100,
      text_position: (c.text_position as HeroTextPosition) ?? "bottom-left",
      title_ar: c.title_ar ?? null,
      title_en: c.title_en ?? null,
      subtitle_ar: c.subtitle_ar ?? null,
      subtitle_en: c.subtitle_en ?? null,
      cta_label_ar: c.cta_label_ar ?? null,
      cta_label_en: c.cta_label_en ?? null,
      cta_href: c.cta_href ?? null,
      scroll_cta_label_ar: c.scroll_cta_label_ar ?? null,
      scroll_cta_label_en: c.scroll_cta_label_en ?? null,
      scroll_cta_href: c.scroll_cta_href ?? null,
      scroll_cta_enabled: c.scroll_cta_enabled ?? null,
    };
  });

export const listHeroCampaigns = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(lazyValidator(() => import("./heroCampaigns.schemas").then((m) => m.heroListSchema), {}))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { assertAdmin } = await import("@/utils/adminAuth.server");
    await assertAdmin(context.userId);

    let q = supabaseAdmin
      .from("hero_campaigns")
      .select("*")
      .order("priority", { ascending: false })
      .order("starts_at", { ascending: false, nullsFirst: false });

    if (!data.anyPage) q = q.eq("page", data.page);
    if (data.market) q = q.eq("market", data.market);


    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);
    return (rows ?? []) as HeroCampaign[];
  });


export const upsertHeroCampaign = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(lazyValidator(() => import("./heroCampaigns.schemas").then((m) => m.heroUpsertSchema)))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { assertAdmin } = await import("@/utils/adminAuth.server");
    await assertAdmin(context.userId);

    const payload = {
      name: data.name.trim(),
      market: data.market,
      page: data.page || "home",
      desktop_url: data.desktop_url || null,
      mobile_url: data.mobile_url || null,
      poster_url: data.poster_url || null,
      starts_at: data.starts_at || null,
      ends_at: data.ends_at || null,
      priority: data.priority ?? 0,
      enabled: data.enabled ?? true,
      height_mode: data.height_mode ?? "full",
      media_opacity: data.media_opacity ?? 100,
      text_position: data.text_position ?? "bottom-left",
      title_ar: data.title_ar ?? null,
      title_en: data.title_en ?? null,
      subtitle_ar: data.subtitle_ar ?? null,
      subtitle_en: data.subtitle_en ?? null,
      cta_label_ar: data.cta_label_ar ?? null,
      cta_label_en: data.cta_label_en ?? null,
      cta_href: data.cta_href ?? null,
      scroll_cta_label_ar: data.scroll_cta_label_ar ?? null,
      scroll_cta_label_en: data.scroll_cta_label_en ?? null,
      scroll_cta_href: data.scroll_cta_href ?? null,
      scroll_cta_enabled: data.scroll_cta_enabled ?? null,
    };

    if (data.id) {
      const { data: row, error } = await supabaseAdmin
        .from("hero_campaigns")
        .update(payload)
        .eq("id", data.id)
        .select()
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!row) throw new Error("الحملة غير موجودة (رُبّما حُذفت) — أعد تحميل الصفحة ثمّ أنشئ حملة جديدة.");


      // Media can be shared across campaigns, devices and other page settings.
      // Saving only replaces references; physical deletion belongs to the
      // separate storage-cleanup workflow after reviewing all remaining uses.

      return row as HeroCampaign;
    }

    const { data: row, error } = await supabaseAdmin
      .from("hero_campaigns")
      .insert(payload)
      .select()
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("تعذّر إنشاء الحملة — لم تُرجع القاعدة أيّ صفّ. حاول مرّة أخرى.");
    return row as HeroCampaign;

  });

export const deleteHeroCampaign = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(lazyValidator(() => import("./heroCampaigns.schemas").then((m) => m.heroIdSchema)))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { assertAdmin } = await import("@/utils/adminAuth.server");
    await assertAdmin(context.userId);

    const { error } = await supabaseAdmin
      .from("hero_campaigns")
      .delete()
      .eq("id", data.id);
    if (error) throw new Error(error.message);

    // احذف كلّ النقاط التفاعليّة المرتبطة بهذه الحملة (ديسكتوب + موبايل + بدون لاحقة)
    try {
      await supabaseAdmin
        .from("image_hotspots")
        .delete()
        .eq("scope", "hero_campaign")
        .in("scope_id", [data.id, `${data.id}:desktop`, `${data.id}:mobile`]);
    } catch {
      // best-effort — لا نُفشل حذف الحملة لو فشل حذف النقاط
    }

    // Keep shared media in storage. Removing this campaign does not establish
    // that its images/videos are unused elsewhere; cleanup is a separate action.

    return { ok: true };
  });
